import { useState } from 'react'

import MailIcon from '@heroicons/react/outline/MailIcon'

import { MemberInvitation } from '@tribeplatform/gql-client/types'
import {
  useInviteMembers,
  useMemberInvitations,
} from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { useDebounce } from '../../../utils/useDebounce'
import { Table } from '../../Table'
import { UserImportModal } from '../../UserImport'
import { emailStatusRenderer } from '../EmailLogs'

export const Invitations = () => {
  const [filter, setFilter] = useState({ query: undefined })
  const debouncedQuery = useDebounce(filter?.query, 300)
  const [inviteMembersModal, setInviteMembersModal] = useState(false)

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useMemberInvitations({
      fields: {
        inviter: { profilePicture: 'basic' },
        emailLog: 'basic',
      },
      variables: {
        limit: 15,
        query: debouncedQuery,
        reverse: true,
      },
      useInfiniteQueryOptions: { keepPreviousData: true },
    })

  const { mutateAsync: inviteMember } = useInviteMembers()

  const onResendInvitation = async (memberInvitation: MemberInvitation) => {
    try {
      await inviteMember({
        input: {
          invitees: [
            {
              name: memberInvitation?.inviteeName,
              email: memberInvitation?.inviteeEmail,
            },
          ],
          invitationMessage: memberInvitation?.invitationMessage,
          roleId: memberInvitation?.roleId,
        },
      })
      toast({
        title: 'Invitation Resent',
        description: 'Invitation is successfully resent.',
        status: 'success',
      })
    } catch (err) {
      toast({
        title: 'Something went wrong',
        description: 'Cannot resend this invitation.',
        status: 'error',
      })
    }
  }

  const columns = [
    {
      Header: 'Email',
      accessor: 'inviteeEmail',
      renderer: 'email',
      width: 'lg',
      visible: true,
    },
    {
      Header: 'Name',
      accessor: 'inviteeName',
      renderer: 'text',
      visible: true,
      width: 'md',
    },
    {
      Header: 'Created at',
      accessor: 'createdAt',
      renderer: 'date',
      width: 'sm',
      visible: true,
    },
    {
      Header: 'Expires at',
      accessor: 'expiresAt',
      renderer: 'date',
      width: 'sm',
      visible: true,
    },
    {
      Header: 'Invited by',
      accessor: 'inviter',
      renderer: 'member',
      width: 'md',
      visible: true,
    },
    {
      Header: 'Status',
      accessor: 'emailLog',
      width: 'sm',
      visible: true,
      renderer: memberInvitation =>
        emailStatusRenderer(memberInvitation?.emailLog),
    },
    {
      visible: true,
      accessor: 'actions',
      locked: true,
      width: 'xs',
      renderer: memberInvitation => (
        <Dropdown>
          <Dropdown.ButtonMinimal />
          <Dropdown.Items>
            <Dropdown.Item
              leadingIcon={<MailIcon />}
              onClick={() => onResendInvitation(memberInvitation)}
            >
              Resend invite
            </Dropdown.Item>
          </Dropdown.Items>
        </Dropdown>
      ),
    },
  ]

  return (
    <>
      <div className="w-full">
        <Table
          isLoading={isLoading}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          columns={columns}
          data={data}
          title="Invitations"
          actions={
            <>
              <Button
                size="lg"
                variant="primary"
                onClick={() => setInviteMembersModal(true)}
              >
                Invite members
              </Button>
            </>
          }
          setFilter={setFilter}
          filter={filter}
        />
      </div>
      {inviteMembersModal ? (
        <UserImportModal open onClose={() => setInviteMembersModal(false)} />
      ) : null}
    </>
  )
}
